import { Select, SelectOptionType, TextInput } from '@octano/global-ui';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Col, Row } from 'reactstrap';

import { TranslationsKeys } from '../../../../../../locales/translations';
import { SectionV2 } from '../../../../../../types/AcademicOffersType';
import { ModalityCodesEnum } from '../../../../../SectionsMaintainer/enums/modality-codes.enum';
import { useAcademicOfferWizard } from '../../../context/AcademicOfferWizardContext';
import { getAvailableCeSections } from '../api';
import { TeacherRow } from './TeacherRow';
import { CourseWithSection } from '../types';

interface TeacherModalityConfig {
  showOnline: boolean;
  showPresential: boolean;
  onlineOnly: boolean;
  presentialOnly: boolean;
}

interface Props {
  course: CourseWithSection;
}

export function SelectedSectionForm({ course }: Props) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const { ceAcademicOfferId } = useAcademicOfferWizard();
  const { control, watch } = useFormContext();

  const selectedSection = watch('selectedSection');
  const selectedSectionData = watch('selectedSectionData');

  const [sectionOptions, setSectionOptions] = useState<SelectOptionType[]>([]);

  useEffect(() => {
    const fetchSections = async () => {
      if (!course || !ceAcademicOfferId) {
        return;
      }

      const { data, error } = await getAvailableCeSections(
        ceAcademicOfferId,
        course.courseId,
      );

      if (error || !data) {
        return;
      }

      const options = data.map((section: SectionV2) => {
        const [code, ...nameParts] = (section.name || '').split('-');
        const name = nameParts.join('-');
        return {
          value: section.id,
          label: section.name,
          data: {
            id: section.id,
            name: name,
            code: code,
            campusName: section.campus?.name || '-',
            maximumStudent: section.maximumStudent || 0,
            scheduleName: section.schedule?.name || '-',
            teachers: (section.sectionProfessorsTeacher || []).map((p) => ({
              teacherId: p.teacherId,
              name: p.teacher.account.fullName || 'Sin nombre',
              isOnline: p.isOnline || false,
              isPresential: p.isPresential || false,
            })),
          },
        };
      });

      setSectionOptions(options);
    };

    fetchSections();
  }, [ceAcademicOfferId, course]);

  // Determinar opciones de modalidad según el tipo de asignatura
  const modalityOptions: TeacherModalityConfig = useMemo(() => {
    const mode = course.modalityDeliveryMode;
    return {
      showOnline:
        mode === ModalityCodesEnum.SP ||
        mode === ModalityCodesEnum.OL ||
        mode === ModalityCodesEnum.OA,
      showPresential:
        mode === ModalityCodesEnum.SP || mode === ModalityCodesEnum.PR,
      onlineOnly:
        mode === ModalityCodesEnum.OL || mode === ModalityCodesEnum.OA,
      presentialOnly: mode === ModalityCodesEnum.PR,
    };
  }, [course.modalityDeliveryMode]);

  return (
    <>
      <Col xs={12} className="mb-3">
        <Select
          name="selectedSection"
          label={t('sections.modal.sectionName')}
          control={control}
          options={sectionOptions}
          isClearable
        />
      </Col>

      {selectedSection && selectedSectionData && (
        <Card className="mb-4">
          <CardBody className="bg-light">
            <h6 className="text-dark mb-3">
              {t('sections.modal.selectedSectionData')}
            </h6>

            <Row>
              <Col xs={12} md={6} className="mb-3">
                <TextInput
                  name="selectedSectionName"
                  label={t('sections.modal.name')}
                  value={selectedSectionData.name}
                  disabled
                />
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <TextInput
                  name="selectedSectionCode"
                  label={t('sections.modal.code')}
                  value={selectedSectionData.code}
                  disabled
                />
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <TextInput
                  name="selectedSectionCampus"
                  label={t('sections.modal.campus')}
                  value={selectedSectionData.campusName}
                  disabled
                />
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <TextInput
                  name="selectedSectionQuota"
                  label={t('sections.modal.quota')}
                  value={selectedSectionData.maximumStudent.toString()}
                  disabled
                />
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <TextInput
                  name="selectedSectionSchedule"
                  label={t('sections.modal.schedule')}
                  value={selectedSectionData.scheduleName}
                  disabled
                />
              </Col>

              <Col xs={12}>
                <label className="form-label text-dark mb-2">
                  {t('sections.modal.teachers')}
                </label>
                {(selectedSectionData.teachers || []).map(
                  (
                    teacher: {
                      isOnline: boolean;
                      isPresential: boolean;
                      id: number;
                      name: string;
                    },
                    index: number,
                  ) => (
                    <div key={index} className="mb-2">
                      <TeacherRow
                        isOnline={teacher.isOnline}
                        isPresential={teacher.isPresential}
                        showOnline={modalityOptions.showOnline}
                        showPresential={modalityOptions.showPresential}
                        disabled={true}
                      >
                        <TextInput
                          name={`selectedTeacher${index}`}
                          label=""
                          value={teacher.name || ''}
                          disabled
                        />
                      </TeacherRow>
                    </div>
                  ),
                )}
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}
    </>
  );
}
